/**
 * @fileoverview StaleVersionNotice: the refresh-and-review message of a refused Sales CRM command.
 * @description Shown in a mutation dialog when the record changed after the dialog loaded it
 * (`412` / `409`). The user refreshes to see the latest state and decides again; the dialog never
 * resends on its own.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import Button from '@/shared/components/atoms/Button'

interface StaleVersionNoticeProps {
  onRefresh: () => void
  isRefreshing: boolean
  /** Replaces the default message, e.g. when the dialog keeps what was typed. */
  message?: string | undefined
}

/**
 * @component
 * @param {StaleVersionNoticeProps} props - The refresh handler and state.
 * @param {() => void} props.onRefresh - Reloads the record's latest version.
 * @param {boolean} props.isRefreshing - Whether that reload is in flight.
 * @param {string} [props.message] - Replaces the default message.
 */
function StaleVersionNotice({ onRefresh, isRefreshing, message }: StaleVersionNoticeProps) {
  const { t } = useTranslation()

  return (
    <div
      role="alert"
      data-testid="stale-version-notice"
      className="flex flex-col gap-2 rounded-lg border border-warning-border bg-warning-bg p-3"
    >
      <p className="text-[13px] text-warning-text">
        {message ?? t('sales.pipeline.stale.message')}
      </p>
      <div className="w-fit">
        <Button
          type="button"
          variant="secondary"
          size="md"
          isLoading={isRefreshing}
          onClick={onRefresh}
          data-testid="stale-version-refresh-button"
        >
          {t('sales.pipeline.stale.refresh')}
        </Button>
      </div>
    </div>
  )
}

export default StaleVersionNotice
